import { notifications } from '@mantine/notifications';
import { IconCheck, IconExclamationCircle } from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { Endpoints } from '@xver/apis';
import type { OAuthLinkChallenge } from '@xver/apis/v1/authentication/model';
import { useAppDispatch } from '@xver/hooks/useAppDispatch';
import { ME_QUERY_KEY } from '@xver/hooks/useMeQuery';
import { requestAuthUser } from '@xver/store/auth/auth.actions';
import { getErrorMessage } from '@xver/utils/error';
import storage from '@xver/utils/storage';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';

import LandingLayout from '@xver/layouts/LandingLayout/LandingLayout';

import { LoadingScreen } from '../../../components/Organisms';
import { GITHUB_AUTHORIZATION_QUERY_KEY } from '../../../hooks/GitHubHooks/useGitHubAuthQuery';
import OAuthLinkVerification from './components/OAuthLinkVerification/OAuthLinkVerification';

const RETURN_TO = '/container-engine?tab=github';
const SIGN_IN = '/';
const ACCOUNT_PROFILE = '/account/profile';

const processedGithubCodes = new Set<string>();

export default function GitHubOAuthCallbackPage() {
    const { t } = useTranslation('containerEngine');
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();
    const queryClient = useQueryClient();
    const hasFired = useRef(false);
    const [challenge, setChallenge] = useState<OAuthLinkChallenge | null>(null);

    const establishSession = useCallback(
        async (token: string, linked: boolean) => {
            storage.set('_zugang', token);

            let user: unknown;
            for (let attempt = 0; attempt < 3 && !user; attempt++) {
                if (attempt > 0) await new Promise((r) => setTimeout(r, 400));
                user = await dispatch(requestAuthUser());
            }
            if (!user) throw new Error('session not established');

            if (linked) {
                notifications.show({
                    title: t('github.login.linkedTitle', 'Account linked'),
                    message: t(
                        'github.login.linkedMessage',
                        'You already had an account with this email, so your GitHub sign-in was linked to it.'
                    ),
                    icon: <IconCheck size={18} color="#22c55e" />,
                });
            }
            navigate('/dashboard', { replace: true });
        },
        [dispatch, navigate, t]
    );

    const handleLinked = useCallback(
        (token: string) => {
            establishSession(token, true).catch((err: { message?: string }) => {
                notifications.show({
                    title: t('github.callback.errorTitle', 'GitHub sign-in failed'),
                    message:
                        err?.message || t('github.toast.connectErrorMessage', 'Please try again.'),
                    icon: <IconExclamationCircle size={18} color="#eb5757" />,
                });
                navigate(SIGN_IN, { replace: true });
            });
        },
        [establishSession, navigate, t]
    );

    useEffect(() => {
        if (hasFired.current) return;
        hasFired.current = true;

        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const mode = sessionStorage.getItem('githubOAuthMode');
        sessionStorage.removeItem('githubOAuthMode');

        const returnFor = (m: string | null) =>
            m === 'login' ? SIGN_IN : m === 'link-account' ? ACCOUNT_PROFILE : RETURN_TO;

        if (!code || !state) {
            notifications.show({
                title: t('github.callback.errorTitle', 'GitHub authorization failed'),
                message: t(
                    'github.callback.missingParamsMessage',
                    'The authorization response was missing required parameters. Please try again.'
                ),
                icon: <IconExclamationCircle size={18} color="#eb5757" />,
            });
            navigate(returnFor(mode), { replace: true });
            return;
        }

        if (processedGithubCodes.has(code)) return;
        processedGithubCodes.add(code);

        if (mode === 'login') {
            Endpoints.v1.Authentication.githubCallback(code, state)
                .then(async (res) => {
                    if (res?.data?.requiresLink) {
                        setChallenge(res.data as OAuthLinkChallenge);
                        return;
                    }

                    const token = res?.data?.token;
                    if (!token) throw new Error('missing token');
                    await establishSession(token, Boolean(res?.data?.linked));
                })
                .catch((err: { message?: string; status?: number }) => {
                    const detail = [err?.status ? `HTTP ${err.status}` : null, err?.message]
                        .filter(Boolean)
                        .join(' — ');
                    notifications.show({
                        title: t('github.callback.errorTitle', 'GitHub sign-in failed'),
                        message:
                            detail || t('github.toast.connectErrorMessage', 'Please try again.'),
                        icon: <IconExclamationCircle size={18} color="#eb5757" />,
                    });
                    navigate(SIGN_IN, { replace: true });
                });
            return;
        }

        if (mode === 'link-account') {
            Endpoints.v1.Authentication.githubLink(code, state)
                .then(async () => {
                    notifications.show({
                        title: t('github.link.successTitle', 'GitHub connected'),
                        message: t(
                            'github.link.successMessage',
                            'Your GitHub account is now connected.'
                        ),
                        icon: <IconCheck size={18} color="#22c55e" />,
                    });
                    await queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
                    dispatch(requestAuthUser());
                })
                .catch((err) => {
                    notifications.show({
                        title: t('github.link.errorTitle', 'GitHub connection failed'),
                        message: getErrorMessage(err),
                        icon: <IconExclamationCircle size={18} color="#eb5757" />,
                    });
                })
                .finally(() => navigate(ACCOUNT_PROFILE, { replace: true }));
            return;
        }

        Endpoints.v1.Kufer.GitHub.completeCallback(code, state)
            .then(async () => {
                notifications.show({
                    title: t('github.callback.successTitle', 'GitHub connected'),
                    message: t(
                        'github.callback.successMessage',
                        'Your GitHub account is now linked to xver-cloud.'
                    ),
                    icon: <IconCheck size={18} color="#22c55e" />,
                });
                await queryClient.invalidateQueries({
                    queryKey: GITHUB_AUTHORIZATION_QUERY_KEY,
                });
            })
            .catch((err: { message?: string; status?: number }) => {
                const detail = [err?.status ? `HTTP ${err.status}` : null, err?.message]
                    .filter(Boolean)
                    .join(' — ');
                notifications.show({
                    title: t('github.callback.errorTitle', 'GitHub authorization failed'),
                    message: detail || t('github.toast.connectErrorMessage', 'Please try again.'),
                    icon: <IconExclamationCircle size={18} color="#eb5757" />,
                });
            })
            .finally(() => {
                navigate(RETURN_TO, { replace: true });
            });
    }, [searchParams, navigate, queryClient, dispatch, t, establishSession]);

    if (challenge) {
        return (
            <LandingLayout>
                <OAuthLinkVerification
                    challenge={challenge}
                    onLinked={handleLinked}
                    onCancel={() => navigate(SIGN_IN, { replace: true })}
                />
            </LandingLayout>
        );
    }

    return <LoadingScreen />;
}
